import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createEditor,
  BaseEditor,
  Transforms,
  Range as SlateRange,
  Editor as SlateEditor,
} from 'slate';
import {
  Slate,
  Editable,
  withReact,
  ReactEditor,
  useSelected,
  useFocused,
  RenderElementProps,
} from 'slate-react';
import {
  ClickAwayListener,
  Popper,
  TextField,
  Tooltip,
} from '@mui/material';
import { textFieldSX } from '../../constants/textFieldCSS';
import { IdValueOption, ResponseConfigOption } from '../../types/common';
import clsx from 'clsx';
import { extractNodeID } from '../extractNodeIDs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DropdownPopper from './DropdownPopper';
import { WorkflowServiceIcon } from './WorkflowServiceIcon';

type CustomPlainTextElement = { type: 'plainText'; children: CustomText[] };
export type CustomTagElement = {
  type: 'tag';
  character: string;
  children: CustomText[];
  dropdownOptionIdToOptionMap: Record<string, IdValueOption>;
  refNodeId?: string;
};
type CustomText = { text: string };

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element:
      | CustomPlainTextElement
      | CustomTagElement;
    Text: CustomText;
  }
}

type InputTextBoxWithDropdownProps = {
    editableInput: string;
    setEditableValue: (value: string, nodeId?: string) => void;
    cssOverrides?: string;
    saveOnBlur?: () => void,
    refNodeId?: string,
    dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption>,
    dropdownOptions?: ResponseConfigOption[],
    countToNameMap: Record<string, string>,
};

const InputTextBoxWithDropdown: React.FC<InputTextBoxWithDropdownProps> = ({
    dropdownOptions,
    dropdownOptionIdToOptionMap,
    editableInput,
    setEditableValue,
    cssOverrides,
    saveOnBlur,
    refNodeId,
    countToNameMap,
}) => {
  const [editor] = React.useState(() => withTags(withReact(createEditor())));
  const [applyToSearchText, setApplyToSearchText] = React.useState('');
  const [target, setTarget] = React.useState<SlateRange | undefined | null>();
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'steps-data-popper' : undefined;

  const renderElement = React.useCallback((props: RenderElementProps) => {
    switch (props.element.type) {
      case 'tag':
        return <TagElement {...props} />;
      default:
        return <DefaultElement {...props} />;
    }
  }, []);

  const transformFromInput = useCallback((inputValue?: string) => {
    const transformedInput: any[] = [];
    let transformedInputLength = 0;

    if (!inputValue || !inputValue?.length) {
      transformedInput.push({ type: 'plainText', children: [{ text: '' }] });
      return transformedInput;
    }

    const parts = inputValue.split(/(\{\{[^}]+\}\}|\n|â†µ)/g);

    parts.forEach((part, index) => {
      if (
        part?.startsWith("{{") && part.endsWith("}}") &&
        !transformedInput[transformedInputLength]?.children?.length
      ) {
        const { nodeId, dataPath } = extractNodeID(part) || {};
        const character = dataPath || part.slice(7, -4);
        const node = nodeId || refNodeId

        transformedInput.push({
          type: 'plainText',
          children: [
            { text: '' },
            {
              type: 'tag',
              character,
              children: [
                {
                  text: '',
                },
              ],
              dropdownOptionIdToOptionMap,
              refNodeId: node,
            },
          ],
        });
      } else if (part?.startsWith("{{") && part.endsWith("}}")) {
        const { nodeId, dataPath } = extractNodeID(part) || {};
        const character = dataPath || part.slice(7, -4);
        const node = nodeId || refNodeId

        transformedInput[transformedInputLength]?.children?.push({
          type: 'tag',
          character,
          children: [
            {
              text: '',
            },
          ],
          dropdownOptionIdToOptionMap,
          refNodeId: node,
        });
      } else if (part === "\n" || part === "â†µ") {
        transformedInputLength++;
        transformedInput.push({ type: 'plainText', children: [{ text: '' }] });
        transformedInputLength++;
      } else if (!transformedInput[transformedInputLength]?.children?.length) {
        transformedInput.push({
          type: 'plainText',
          children: [{ text: part }],
        });
      } else {
        transformedInput[transformedInputLength]?.children?.push({
          text: part,
        });
      }

      if (
        part?.startsWith("{{") && part.endsWith("}}") &&
        (!inputValue[index + 1] ||
          (part[index + 1]?.startsWith("{{") && part[index+1].endsWith("}}")) ||
          part[index + 1] === "\n" ||
          part[index + 1] === "â†µ")
      ) {
        transformedInput[transformedInputLength]?.children?.push({
          text: '',
        });
      }
    });

    return transformedInput;
  }, [dropdownOptionIdToOptionMap, refNodeId]);

  const [initialValue, setInitialValue] = useState<any[]>(transformFromInput(editableInput));

  // this handles if the initial value is not set immediately
  // todo: use controlled component in the future
  useEffect(() => {
    const transformedInput = transformFromInput(editableInput);
    setInitialValue(transformedInput);
  }, [editableInput, transformFromInput]);

  const transformToOutput = (inputValue: any[]) => {
    let transformedOutput: string = '';
    let nodeId: string | undefined = undefined;

    inputValue.forEach((individualValue) => {
      if (
        individualValue?.children?.length === 1 &&
        individualValue.children[0].text === ''
      ) {
        // Don't add newlines for empty elements - this prevents \n from being added on clicks
        // transformedOutput+= "\n";
      } else {
        individualValue.children.forEach((child: any) => {
          if (child.type === 'tag') {
            nodeId = child.refNodeId;
            transformedOutput+= "{{ jp `"+nodeId+":"+ child.character + "` }}";
          } else {
            transformedOutput+= child.text;
          }
        });
      }
    });

    return { transformedOutput, nodeId };
  };

  const filteredDropdownOptions = dropdownOptions?.filter(
    (dropdownOption) =>
      dropdownOption.id
        .toLowerCase()
        .includes(applyToSearchText.toLowerCase())
  );

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const initialState: Record<string, boolean> = {};
    dropdownOptions?.forEach((option) => {
      const count = option.count?.toString() || 'Other';
      initialState[count] = true;
    });
    setExpandedGroups(initialState);
  }, [dropdownOptions]);

  const [isFocused, setIsFocused] = useState(false);

  const groupByCount = useMemo(() => {
    const groups: Record<string, typeof filteredDropdownOptions> = {};
    filteredDropdownOptions?.forEach((option) => {
      const count = option.count?.toString() || 'Other';
      if (!groups[count]) {
        groups[count] = [];
      }
      groups[count].push(option);
    });
    return groups;
  }, [filteredDropdownOptions]);

  return (
        <ClickAwayListener onClickAway={() => {
            setAnchorEl(null);
            setApplyToSearchText('');
            setIsFocused((prev) => {
              if (prev) {
                saveOnBlur && saveOnBlur();
              }
              return false;
            });
        }}>
            <div
                onClick={(e) => {
                    setIsFocused(true);
                    setAnchorEl(e.currentTarget);
                    if (e.target === e.currentTarget) {
                    ReactEditor.focus(editor);
                    Transforms.select(editor, SlateEditor.end(editor, []));
                    }
                }}
                className={clsx('w-full relative')}
                style={{ position: 'relative' }}
            >
                <Slate
                    key={JSON.stringify(initialValue)}
                    editor={editor}
                    initialValue={initialValue}
                    onChange={(value) => {
                        const { selection } = editor;
                        const { transformedOutput, nodeId } = transformToOutput(value);
                        
                        // Only update if the content actually changed to prevent unnecessary re-renders
                        // Defer the state update to avoid setState during render warning
                        if (transformedOutput !== editableInput) {
                            setTimeout(() => setEditableValue(transformedOutput, nodeId), 0);
                        }
                        
                        if (selection && SlateRange.isCollapsed(selection)) {
                            try {
                                const [start] = SlateRange.edges(selection);
                                const wordBefore = SlateEditor.before(editor, start, {
                                    unit: 'word',
                                });
                                const before =
                                    wordBefore && SlateEditor.before(editor, wordBefore);
                                const beforeRange =
                                    before && SlateEditor.range(editor, before, start);
                                const beforeText =
                                    beforeRange && SlateEditor.string(editor, beforeRange);
                                const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);
                                const after = SlateEditor.after(editor, start);
                                const afterRange = SlateEditor.range(editor, start, after);
                                const afterText = SlateEditor.string(editor, afterRange);
                                const afterMatch = afterText.match(/^(\s|$)/);

                                if (beforeMatch && afterMatch) {
                                    setTarget(beforeRange);
                                    return;
                                }
                            } catch (e) {
                                // Invalid selection/path, reset to end of document
                                try {
                                    const end = SlateEditor.end(editor, []);
                                    Transforms.select(editor, end);
                                } catch (e2) {
                                    // Editor might be in invalid state, ignore
                                }
                            }
                        }

                        setTarget(null);
                    }}
                >
                    <Editable
                        renderElement={renderElement}
                        className='tracking-wide text-xs max-h-40 overflow-y-auto border border-solid border-borderGrey rounded-lg bg-white py-3 px-4 focus:outline-0 focus:border focus:border-nanonetsBlue focus:shadow-lightBlueShadow'
                    />
                </Slate>

                <DropdownPopper
                    open={open}
                    anchorEl={anchorEl}
                    options={dropdownOptions || []}
                    onClose={() => setAnchorEl(null)}
                    onOptionClick={(dropdownOption) => {
                      target && Transforms.select(editor, target);
                      insertTag(
                          editor,
                          dropdownOption.id,
                          dropdownOptionIdToOptionMap,
                          dropdownOption.refNodeId,
                      );
                      setTarget(null);
                  }}
                />
                
            </div>
        </ClickAwayListener>
    );
};

export default InputTextBoxWithDropdown;

const withTags = (editor: SlateEditor) => {
  const { isInline, isVoid, markableVoid } = editor;

  editor.isInline = (element) => {
    return element.type === 'tag' ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === 'tag' ? true : isVoid(element);
  };

  editor.markableVoid = (element) => {
    return element.type === 'tag' || markableVoid(element);
  };

  return editor;
};

export const insertTag = (
  editor: SlateEditor,
  character: string,
  dropdownOptionIdToOptionMap: Record<string, IdValueOption>,
  refNodeId?: string,
) => {
  const tag: CustomTagElement = {
    type: 'tag',
    character,
    children: [{ text: '' }],
    dropdownOptionIdToOptionMap,
    refNodeId,
  };
  Transforms.insertNodes(editor, tag);
  Transforms.move(editor);
};

export const TagElement = ({ attributes, children, element }: any) => {
  const selected = useSelected();
  const focused = useFocused();
  const [imgError, setImgError] = useState(false);
  
  const style: React.CSSProperties = {
    padding: '2px 4px',
    margin: '2px 1px',
    verticalAlign: 'baseline',
    display: 'inline-block',
    borderRadius: '4px',
    backgroundColor: '#F5F6F8',
    fontSize: '12px',
    border: '1px solid #EBECF0',
    boxShadow: selected && focused ? '0 0 0 2px #d6d8e0' : 'none',
    position: 'relative',
    top: '5px',
  };

  const optionDetails = element.dropdownOptionIdToOptionMap?.[`${element.character}:${element.refNodeId}`] || undefined;

  return (
    <span
      {...attributes}
      contentEditable={false}
      style={style}
    >
      {
        optionDetails !== undefined ?
            <Tooltip 
              title={`${optionDetails.id}${optionDetails.value ? `: ${optionDetails.value}` : ''}`}
              placement="top"
              arrow
            >
              <div className='flex items-center gap-1 max-w-[200px]'>
                <WorkflowServiceIcon
                  workflowType={optionDetails.valueDetail || ''} alt={optionDetails.valueDetail}
                  height={20}
                  width={20}
                />
                <div className='text-greyishBlue'>{optionDetails.count ? `${optionDetails.count} |` : ''}</div>
                <div className='truncate'>{optionDetails.id}</div>
                {optionDetails.value && (
                  <div className='text-greyishBlueLight truncate max-w-[100px]'>
                    {optionDetails.value.length > 20 ? `${optionDetails.value.substring(0, 20)}...` : optionDetails.value}
                  </div>
                )}
              </div>
            </Tooltip>
            : element.character
      }
      {children}
    </span>
  );
};

export const DefaultElement = (props: any) => {
  return (
    <span {...props.attributes} className='relative'>
      {props.children}
    </span>
  );
};
